<script>
  /* global DEV_ENV */
  import Loading from '../common/Loading.svelte';
  import Error from '../common/Error.svelte';
  import AdminFields from '../ui-components/AdminFields.svelte';
  import HiBars3 from '../heroicons/HiBars3.svelte';
  import TwButtonSecondary from '../tailwind/TwButtonSecondary.svelte';
  import TwAlert from '../tailwind/TwAlert.svelte';
  import TwButtonPrimary from '../tailwind/TwButtonPrimary.svelte';
  import Common from '../js/common.js';
  import Validation from '../js/validation.js';

  import Sortable from 'sortablejs';

  export let page;
  export let fields;
  export let lang = undefined;
  export let user;

  user = JSON.parse(window.localStorage.getItem('user'));
  if (DEV_ENV) user = { firstName: 'Jan', lastName: 'Novák', roles: ['ROLE_ADMIN'] };
  if (!user.roles.includes('ROLE_ADMIN')) window.location.href = window.localStorage.getItem('previousPage') || './';

  let values;

  const defaultValue = {};

  fields.forEach(field => {
    if (field.name.includes('.')) {
      const primaryKey = field.name.split('.')[0];
      const secondaryKey = field.name.split('.')[1];

      if (!defaultValue[primaryKey]) defaultValue[primaryKey] = {};

      defaultValue[primaryKey][secondaryKey] = undefined;
    } else {
      defaultValue[field.name] = undefined;
    }
  });

  defaultValue.expanded = true;
  defaultValue.deleted = false;

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/admin/${page}/get`);

    if (response.ok) {
      const data = await response.json();

      values = data.data;
      user = data.user;

      if (user) {
        window.localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        window.localStorage.removeItem('user');

        back();
      }

      values.forEach(value => {
        Object.keys(value).forEach(key => {
          if (value[key] === null) {
            value[key] = undefined;
          } else if (typeof value[key] === 'object') {
            Object.keys(value[key]).forEach(nestedKey => {
              if (value[key][nestedKey] === null) value[key][nestedKey] = undefined;

              if (Array.isArray(value[key][nestedKey])) value[key][nestedKey] = value[key][nestedKey].join(',');
            });
          }

          if (Array.isArray(value[key])) value[key] = value[key].join(',');
        });

        value.expanded = false;
        value.deleted = false;
      });
    } else {
      if (response.status === Common.UNAUTHORIZED) window.location.href = './';
      else error = true;
    }

    loading = false;
  })();

  let alertType;
  let alertMessage;

  let adminPageBoxElement;
  let sortable;

  $: if (adminPageBoxElement) {
    sortable = Sortable.create(adminPageBoxElement, {
      handle: '.admin-page-box-group-main-expand-handle',
      ghostClass: 'admin-page-box-ghost',
    });
  }

  let errors = [];

  function saveValues () {
    errors = [];

    alertType = 'info';
    alertMessage = {
      cs: i18n.savingInProgress.cs,
      en: i18n.savingInProgress.en,
    };

    const order = sortable ? sortable.toArray().map(Number) : [];
    const valuesInternal = Common.deepCopy(values).filter(value => !value.deleted);

    valuesInternal.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)).forEach((value, index) => {
      value.id = index + 1;

      delete value.expanded;
      delete value.deleted;
    });

    try {
      // eslint-disable-next-line no-undef
      const formData = new FormData();

      const data = [];

      valuesInternal.forEach((value, idx) => {
        try {
          data.push(Validation.convertAndCheck(fields, true, value));
        } catch (error) {
          error.errors.forEach(error => {
            error.message.cs = `${Common.addZeros(order[idx])}: ${error.message.cs}`;
            error.message.en = `${Common.addZeros(order[idx])}: ${error.message.en}`;
          });

          throw error;
        }
      });

      formData.append('data', JSON.stringify(data));

      (async () => {
        // eslint-disable-next-line no-undef
        const response = await fetch(`${Common.BACKEND_URL}/admin/${page}/edit`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          alertType = 'success';
          alertMessage = {
            cs: 'Úpravy byly úspěšně uloženy.',
            en: 'Changes have been successfully saved.',
          };
        } else {
          alertType = 'danger';
          alertMessage = {
            cs: i18n.unexpectedError.cs,
            en: i18n.unexpectedError.en,
          };
        }
      })();
    } catch (error) {
      errors = error.errors;

      alertType = 'danger';
      alertMessage = {
        cs: errors[0].message.cs,
        en: errors[0].message.en,
      };
    }
  }

  function back () {
    window.location.href = window.localStorage.getItem('previousPage') || './';
  }

  const i18n = {
    add: {
      cs: 'Přidat',
      en: 'Add',
    },
    save: {
      cs: 'Uložit',
      en: 'Save',
    },
    savingInProgress: {
      cs: 'Probíhá ukládání úprav...',
      en: 'Changes saving in progress...',
    },
    unexpectedError: {
      cs: '(Ne)očekávaná chyba. `E29`',
      en: '(Un)expected error. `E29`',
    },
    onlyForAdministrators: {
      cs: 'Tato stránka je pouze pro administrátory. Pokud nejste administrátor, prosím opusťte tuto stránku.',
      en: 'This page is only for administrators. If you are not an administrator, please leave this page.',
    },
  };

  alertType = 'warning';
  alertMessage = i18n.onlyForAdministrators;
</script>

<div class='admin-page'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    {#if values.filter(value => !value.deleted).length > 0}
      <div class='admin-page-box' bind:this={adminPageBoxElement}>
        {#each values.filter(value => !value.deleted) as value}
          <div class='admin-page-box-group' data-id={value.id}>
            <div class='admin-page-box-group-main'>
              <div class='admin-page-box-group-main-id'>
                {Common.addZeros(value.id)}
              </div>

              <AdminFields bind:value={value} bind:lang
                fields={fields.filter(field => field.main)}/>

              <div class='admin-page-box-group-main-expand'>
                <div class='admin-page-box-group-main-expand-handle'>
                  <HiBars3 cssClass='h-5 w-5'/>
                </div>

                <div class='admin-page-box-group-main-expand-button'>
                  <TwButtonSecondary icon={value.expanded ? 'chevron-up' : 'chevron-down'}
                    onClick={() => { value.expanded = !value.expanded; }}/>
                </div>
              </div>
            </div>

            {#if value.expanded}
              <div class='admin-page-box-group-expanded'>
                <AdminFields bind:value={value} bind:lang
                  fields={fields.filter(field => !field.main)}/>

                <div class='admin-page-box-group-expanded-delete'>
                  <div class='admin-page-box-group-expanded-delete-button'>
                    <TwButtonSecondary icon='trash'
                      onClick={() => { value.deleted = true; values = values; }}/>
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <div class='admin-page-alert-and-controls'>
      <div class='admin-page-alert-and-controls-alert'>
        <TwAlert type={alertType} message={alertMessage[lang]}/>
      </div>

      <div class='admin-page-alert-and-controls-controls'>
        <div class='admin-page-alert-and-controls-controls-add'>
          <TwButtonSecondary value={i18n.add[lang]} icon='plus'
            onClick={() => { values = [...values, { id: values.length + 1, ...Common.deepCopy(defaultValue) }]; }}/>
        </div>

        <div class='admin-page-alert-and-controls-controls-save'>
          <TwButtonPrimary value={i18n.save[lang]} icon='check'
            onClick={() => { saveValues(); }}/>
        </div>
      </div>
    </div>
  {/if}
</div>


<style>
  .admin-page {
    display: flex;
    flex-direction: column;
    gap: 40px;

    max-width: 1440px;
    min-height: calc(30vh - 180px);

    padding: 40px 60px;

    margin: auto;
  }

  .admin-page-box {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  :global(.admin-page-box-ghost) {
    opacity: 0.5;
  }

  .admin-page-box-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    padding: 10px;

    border-radius: 5px;

    background: var(--grey-1);
  }

  .admin-page-box-group-main {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;

    width: 100%;
  }

  .admin-page-box-group-main-id {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 42px;
    height: 36px;

    font-weight: bold;
  }

  .admin-page-box-group-main-expand {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    gap: 8px;
  }

  .admin-page-box-group-main-expand-handle {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 42px;
    height: 24px;

    cursor: move;
  }

  .admin-page-box-group-expanded {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;

    width: 100%;
  }

  .admin-page-box-group-expanded-delete {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;

    gap: 8px;
  }

  .admin-page-alert-and-controls {
    display: flex;
    gap: 40px;
  }

  .admin-page-alert-and-controls-alert {
    flex-grow: 1;

    line-height: 1.1;
  }

  .admin-page-alert-and-controls-controls {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  :global(body.dark) .admin-page-box-group {
    background: var(--grey-8);
  }

  @media only screen and (max-width: 900px) {
    .admin-page {
      padding: 40px;
    }
  }

  @media only screen and (max-width: 640px) {
    .admin-page-alert-and-controls {
      flex-direction: column;
    }
  }

  @media only screen and (max-width: 350px) {
    .admin-page {
      padding: 40px 20px;
    }
  }
</style>
